import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { buildsGridConfig } from '@/features/builds/grid/gridConfig';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import { Stack } from '@/ui';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { buildBuildsColumns } from './buildsColumns';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColDef, GridFilterModel, GridSortModel } from '@/ui/primitives';
import type { BuildDto, QueryResult } from '@flowqa/shared';

type BuildsTableProps = {
  fixedFilters?: GridQueryInput['filters'];
  noRowsText?: string;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<BuildDto>>;
  queryKey?: QueryKey;
  onRowsChange?: (rows: BuildDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  showPipelineColumn?: boolean;
};

export const BuildsTable = ({
  fixedFilters,
  noRowsText = 'No builds found',
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  onRowsChange,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  showPipelineColumn = true,
}: BuildsTableProps): ReactElement => {
  const { session } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const columns: Array<GridColDef<BuildDto>> = useMemo(
    () =>
      buildBuildsColumns({
        showPipelineColumn,
        durationThreshold: thresholds.pipelines.avgDuration,
      }),
    [showPipelineColumn, thresholds.pipelines.avgDuration],
  );

  const gridConfig = buildsGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey ?? 'builds-table',
    fixedFilters,
    initialSortModel,
    initialFilterModel,
  });
  const gridProps = buildTableGridProps<BuildDto>({
    gridConfig,
    queryFn,
    queryKey,
    pageSizeOptions,
    initialPageSize,
    disablePageSizeSelect,
    noRowsText,
    onRowsChange,
    onQueryInput,
    onDataUpdatedAt,
    showLastUpdated,
  });

  return (
    <Stack gap="md">
      <ServerDataTable<BuildDto>
        queryPath="builds/query"
        {...gridConfig}
        {...gridProps}
        columns={columns}
        getRowId={(row) => row.id}
        token={session?.tokens.accessToken ?? null}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
      />
    </Stack>
  );
};
